/**
 * 지문인식 관련 Composable
 * WebAuthn API를 활용한 지문 등록 및 인증 기능 제공
 */

export interface FingerprintInfo {
  fingerprintId: string;        // 지문 고유 ID (UUID)
  userId: string;                // 매핑된 사용자 ID
  userSeq: number;               // 사용자 시퀀스
  deviceId: string;              // 기기 번호 (userDeviceId)
  credentialId: string;          // WebAuthn Public Key Credential ID
  registeredAt: string;          // 등록 일시 (ISO 8601)
  isActive: boolean;             // 활성화 여부
}

const FINGERPRINT_STORAGE_KEY = 'fingerprintMappings';
const FINGERPRINT_ENABLED_KEY = 'isFingerprintEnabled';

/**
 * WebAuthn API 지원 여부 확인
 */
export function isWebAuthnSupported(): boolean {
  if (typeof window === 'undefined') return false;
  return typeof window.PublicKeyCredential !== 'undefined';
}

/**
 * WebAuthn API 지원 환경 진단 정보 수집
 */
export function getWebAuthnDiagnostics(): {
  isSupported: boolean;
  details: {
    publicKeyCredential: boolean;
    navigatorCredentials: boolean;
    isHttps: boolean;
    isLocalhost: boolean;
    protocol: string;
    hostname: string;
    userAgent: string;
    isStandalone: boolean;
    isWebView: boolean;
    browserInfo: string;
  };
} {
  if (typeof window === 'undefined') {
    return {
      isSupported: false,
      details: {
        publicKeyCredential: false,
        navigatorCredentials: false,
        isHttps: false,
        isLocalhost: false,
        protocol: 'unknown',
        hostname: 'unknown',
        userAgent: 'unknown',
        isStandalone: false,
        isWebView: false,
        browserInfo: 'Server-side rendering',
      },
    };
  }

  const publicKeyCredential = typeof window.PublicKeyCredential !== 'undefined';
  const navigatorCredentials = typeof navigator.credentials !== 'undefined';
  const protocol = window.location.protocol;
  const isHttps = protocol === 'https:';
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const hostname = window.location.hostname;
  const userAgent = navigator.userAgent;
  
  // 앱 웹뷰 여부 확인
  const isStandalone = (window.navigator as any).standalone === true || 
                       window.matchMedia('(display-mode: standalone)').matches;
  const isWebView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(userAgent) ||
                    /wv/i.test(userAgent) ||
                    /Android.*Version\/\d/i.test(userAgent);

  // 브라우저 정보 추출
  let browserInfo = 'Unknown';
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    const match = userAgent.match(/Chrome\/(\d+)/);
    browserInfo = match ? `Chrome ${match[1]}` : 'Chrome';
  } else if (userAgent.includes('Edg')) {
    const match = userAgent.match(/Edg\/(\d+)/);
    browserInfo = match ? `Edge ${match[1]}` : 'Edge';
  } else if (userAgent.includes('Firefox')) {
    const match = userAgent.match(/Firefox\/(\d+)/);
    browserInfo = match ? `Firefox ${match[1]}` : 'Firefox';
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    const match = userAgent.match(/Version\/(\d+)/);
    browserInfo = match ? `Safari ${match[1]}` : 'Safari';
  }

  return {
    isSupported: publicKeyCredential && navigatorCredentials && (isHttps || isLocalhost),
    details: {
      publicKeyCredential,
      navigatorCredentials,
      isHttps,
      isLocalhost,
      protocol,
      hostname,
      userAgent,
      isStandalone,
      isWebView,
      browserInfo,
    },
  };
}

/**
 * WebAuthn API를 사용한 지문 등록
 */
export async function registerFingerprintWebAuthn(
  userId: string,
  userSeq: number,
  deviceId: string
): Promise<FingerprintInfo> {
  const diagnostics = getWebAuthnDiagnostics();
  if (!diagnostics.isSupported) {
    const { details } = diagnostics;
    const reasons: string[] = [];
    
    if (!details.publicKeyCredential) {
      reasons.push('PublicKeyCredential API 미지원');
    }
    if (!details.navigatorCredentials) {
      reasons.push('navigator.credentials API 미지원');
    }
    if (!details.isHttps && !details.isLocalhost) {
      reasons.push(`HTTPS가 아닙니다 (현재: ${details.protocol})`);
    }
    if (details.isWebView) {
      reasons.push('앱 웹뷰 환경 (네이티브 API 사용 필요)');
    }
    
    const detailMessage = `
원인: ${reasons.join(', ') || '알 수 없는 이유'}
프로토콜: ${details.protocol}
호스트: ${details.hostname}
브라우저: ${details.browserInfo}
웹뷰 여부: ${details.isWebView ? '예' : '아니오'}
독립 앱 여부: ${details.isStandalone ? '예' : '아니오'}
    `.trim();
    
    throw new Error(`WebAuthn API가 지원되지 않는 환경입니다.\n\n${detailMessage}`);
  }

  try {
    // PublicKeyCredential 생성 옵션
    const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
      challenge: Uint8Array.from(
        crypto.getRandomValues(new Uint8Array(32))
      ),
      rp: {
        name: 'REMS',
        id: window.location.hostname,
      },
      user: {
        id: Uint8Array.from(userId, (c) => c.charCodeAt(0)),
        name: userId,
        displayName: userId,
      },
      pubKeyCredParams: [
        { alg: -7, type: 'public-key' }, // ES256
        { alg: -257, type: 'public-key' }, // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification: 'required',
      },
      timeout: 60000,
      attestation: 'direct',
    };

    // 지문 등록 요청
    const credential = await navigator.credentials.create({
      publicKey: publicKeyCredentialCreationOptions,
    }) as PublicKeyCredential;

    if (!credential) {
      throw new Error('지문 등록에 실패했습니다.');
    }

    // Credential ID를 base64url로 인코딩
    const credentialId = arrayBufferToBase64Url(credential.rawId);

    // 지문 정보 생성
    const fingerprintInfo: FingerprintInfo = {
      fingerprintId: crypto.randomUUID(),
      userId,
      userSeq,
      deviceId,
      credentialId,
      registeredAt: new Date().toISOString(),
      isActive: true,
    };

    return fingerprintInfo;
  } catch (error) {
    const err = error as Error;
    if (err.name === 'NotAllowedError') {
      throw new Error('지문 등록이 취소되었습니다.');
    } else if (err.name === 'NotSupportedError') {
      throw new Error('이 기기에서 지문 인증을 지원하지 않습니다.');
    } else if (err.name === 'InvalidStateError') {
      throw new Error('이미 등록된 지문입니다.');
    } else {
      throw new Error(`지문 등록 중 오류가 발생했습니다: ${err.message}`);
    }
  }
}

/**
 * WebAuthn API를 사용한 지문 인증
 */
export async function authenticateFingerprintWebAuthn(): Promise<string> {
  const diagnostics = getWebAuthnDiagnostics();
  if (!diagnostics.isSupported) {
    const { details } = diagnostics;
    const reasons: string[] = [];
    
    if (!details.publicKeyCredential) {
      reasons.push('PublicKeyCredential API 미지원');
    }
    if (!details.navigatorCredentials) {
      reasons.push('navigator.credentials API 미지원');
    }
    if (!details.isHttps && !details.isLocalhost) {
      reasons.push(`HTTPS가 아닙니다 (현재: ${details.protocol})`);
    }
    if (details.isWebView) {
      reasons.push('앱 웹뷰 환경 (네이티브 API 사용 필요)');
    }
    
    const detailMessage = `
원인: ${reasons.join(', ') || '알 수 없는 이유'}
프로토콜: ${details.protocol}
호스트: ${details.hostname}
브라우저: ${details.browserInfo}
웹뷰 여부: ${details.isWebView ? '예' : '아니오'}
독립 앱 여부: ${details.isStandalone ? '예' : '아니오'}
    `.trim();
    
    throw new Error(`WebAuthn API가 지원되지 않는 환경입니다.\n\n${detailMessage}`);
  }

  try {
    // 등록된 지문 목록 가져오기
    const mappings = getFingerprintMappings();
    if (mappings.length === 0) {
      throw new Error('등록된 지문이 없습니다.');
    }

    // 등록된 credential ID 목록 생성
    const allowCredentials = mappings
      .filter(m => m.isActive)
      .map(m => ({
        id: base64UrlToArrayBuffer(m.credentialId),
        type: 'public-key' as const,
      }));

    // PublicKeyCredential 요청 옵션
    const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
      challenge: Uint8Array.from(
        crypto.getRandomValues(new Uint8Array(32))
      ),
      allowCredentials,
      timeout: 60000,
      userVerification: 'required',
    };

    // 지문 인증 요청
    const assertion = await navigator.credentials.get({
      publicKey: publicKeyCredentialRequestOptions,
    }) as PublicKeyCredential;

    if (!assertion) {
      throw new Error('지문 인증에 실패했습니다.');
    }

    // Credential ID를 base64url로 인코딩
    const credentialId = arrayBufferToBase64Url(assertion.rawId);

    return credentialId;
  } catch (error) {
    const err = error as Error;
    if (err.name === 'NotAllowedError') {
      throw new Error('지문 인증이 취소되었습니다.');
    } else if (err.name === 'NotSupportedError') {
      throw new Error('이 기기에서 지문 인증을 지원하지 않습니다.');
    } else {
      throw new Error(`지문 인증 중 오류가 발생했습니다: ${err.message}`);
    }
  }
}

/**
 * ArrayBuffer를 base64url로 변환
 */
function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * FIDO2 등록 (앱 환경)
 */
export async function registerFingerprintFido2(
  userId: string,
  userSeq: number,
  deviceId: string
): Promise<FingerprintInfo> {
  try {
    // 1. 서버에서 Challenge 요청
    const challengeResponse = await fetch('/api/auth/fido2/challenge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'register' })
    });

    if (!challengeResponse.ok) {
      throw new Error('Challenge 요청 실패');
    }

    const { challenge, rpId } = await challengeResponse.json();

    // 2. 앱 네이티브 FIDO2 API 호출
    const nuxtApp = useNuxtApp();
    // @ts-ignore
    const mobileHandle = nuxtApp.$mobileHandle as any;
    
    if (!mobileHandle || !mobileHandle.registerFido2Credential) {
      throw new Error('FIDO2 등록 API를 사용할 수 없습니다. 앱 측 구현을 확인해주세요.');
    }

    const credentialResponse = await mobileHandle.registerFido2Credential(
      challenge,
      rpId,
      userId,
      userId
    );

    if (!credentialResponse) {
      throw new Error('FIDO2 등록 응답을 받을 수 없습니다.');
    }

    const credentialData = typeof credentialResponse === 'string' 
      ? JSON.parse(credentialResponse) 
      : credentialResponse;

    const { credentialId, rawAttestationObject, clientDataJSON } = credentialData;

    // 3. 서버에 등록 요청
    const registerResponse = await fetch('/api/auth/fido2/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        challenge,
        credentialId,
        attestationObject: rawAttestationObject,
        clientDataJSON,
        userId
      })
    });

    if (!registerResponse.ok) {
      const errorData = await registerResponse.json().catch(() => ({}));
      throw new Error(errorData.message || 'FIDO2 등록 실패');
    }

    // 4. 지문 정보 생성
    const fingerprintInfo: FingerprintInfo = {
      fingerprintId: crypto.randomUUID(),
      userId,
      userSeq,
      deviceId,
      credentialId,
      registeredAt: new Date().toISOString(),
      isActive: true,
    };

    // 5. 매핑 저장
    saveFingerprintMapping(fingerprintInfo);

    return fingerprintInfo;
  } catch (error) {
    const err = error as Error;
    console.error('FIDO2 등록 오류:', err);
    throw new Error(`FIDO2 등록 실패: ${err.message}`);
  }
}

/**
 * FIDO2 인증 (앱 환경)
 */
export async function authenticateFingerprintFido2(): Promise<string> {
  try {
    // 1. 등록된 지문 목록 가져오기
    const mappings = getFingerprintMappings();
    if (mappings.length === 0) {
      throw new Error('등록된 지문이 없습니다.');
    }

    // 2. 서버에서 Challenge 요청
    const challengeResponse = await fetch('/api/auth/fido2/challenge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'authenticate' })
    });

    if (!challengeResponse.ok) {
      throw new Error('Challenge 요청 실패');
    }

    const { challenge, rpId } = await challengeResponse.json();

    // 3. 앱 네이티브 FIDO2 API 호출
    const nuxtApp = useNuxtApp();
    // @ts-ignore
    const mobileHandle = nuxtApp.$mobileHandle as any;
    
    if (!mobileHandle || !mobileHandle.authenticateFido2) {
      throw new Error('FIDO2 인증 API를 사용할 수 없습니다. 앱 측 구현을 확인해주세요.');
    }

    const allowCredentialIds = mappings
      .filter(m => m.isActive)
      .map(m => m.credentialId);

    const assertionResponse = await mobileHandle.authenticateFido2(
      challenge,
      rpId,
      allowCredentialIds
    );

    if (!assertionResponse) {
      throw new Error('FIDO2 인증 응답을 받을 수 없습니다.');
    }

    const assertionData = typeof assertionResponse === 'string'
      ? JSON.parse(assertionResponse)
      : assertionResponse;

    const { credentialId, authenticatorData, clientDataJSON, signature } = assertionData;

    // 4. 서버에 인증 요청
    const authenticateResponse = await fetch('/api/auth/fido2/authenticate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        challenge,
        credentialId,
        authenticatorData,
        clientDataJSON,
        signature
      })
    });

    if (!authenticateResponse.ok) {
      const errorData = await authenticateResponse.json().catch(() => ({}));
      throw new Error(errorData.message || 'FIDO2 인증 실패');
    }

    const { userId } = await authenticateResponse.json();
    return userId;
  } catch (error) {
    const err = error as Error;
    console.error('FIDO2 인증 오류:', err);
    throw new Error(`FIDO2 인증 실패: ${err.message}`);
  }
}

/**
 * base64url을 ArrayBuffer로 변환
 */
function base64UrlToArrayBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * localStorage에서 지문 매핑 목록 조회
 */
export function getFingerprintMappings(): FingerprintInfo[] {
  if (typeof window === 'undefined') return [];
  
  try {
    const stored = localStorage.getItem(FINGERPRINT_STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored) as FingerprintInfo[];
  } catch (error) {
    console.error('지문 매핑 목록 조회 실패:', error);
    return [];
  }
}

/**
 * localStorage에 지문 매핑 저장
 */
export function saveFingerprintMapping(fingerprintInfo: FingerprintInfo): void {
  if (typeof window === 'undefined') return;

  try {
    const mappings = getFingerprintMappings();
    mappings.push(fingerprintInfo);
    localStorage.setItem(FINGERPRINT_STORAGE_KEY, JSON.stringify(mappings));
  } catch (error) {
    console.error('지문 매핑 저장 실패:', error);
    throw new Error('지문 등록 정보 저장에 실패했습니다.');
  }
}

/**
 * credentialId로 지문 매핑 조회
 */
export function getFingerprintByCredentialId(credentialId: string): FingerprintInfo | null {
  const mappings = getFingerprintMappings();
  return mappings.find(m => m.credentialId === credentialId && m.isActive) || null;
}

/**
 * 지문 매핑 삭제
 */
export function deleteFingerprintMapping(fingerprintId: string): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const mappings = getFingerprintMappings();
    const filtered = mappings.filter(m => m.fingerprintId !== fingerprintId);
    localStorage.setItem(FINGERPRINT_STORAGE_KEY, JSON.stringify(filtered));
    return true;
  } catch (error) {
    console.error('지문 매핑 삭제 실패:', error);
    return false;
  }
}

/**
 * 지문인식 활성화 여부 확인
 */
export function isFingerprintEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(FINGERPRINT_ENABLED_KEY) === 'true';
}

/**
 * 지문인식 활성화 설정
 */
export function setFingerprintEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(FINGERPRINT_ENABLED_KEY, enabled ? 'true' : 'false');
}

/**
 * 등록된 지문이 있는지 확인
 */
export function hasRegisteredFingerprints(): boolean {
  const mappings = getFingerprintMappings();
  return mappings.filter(m => m.isActive).length > 0;
}
